import styled, { css } from 'styled-components'
import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import { Transition, TransitionStatus } from 'react-transition-group'
import getLayer from 'styles/layer'

interface TransitionProps {
  state: TransitionStatus
  position?: 'top' | 'center' | 'bottom'
}

const dialogWrapperAnimate = (props: TransitionProps) => {
  const { state } = props
  if (state === 'entered') {
    return css`
      opacity: 1;
      pointer-events: visible;
    `
  }
  return css`
    opacity: 0;
  `
}

const DialogWrapper = styled.div<TransitionProps>`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100dvh;

  background-color: ${(p) => p.theme.DIALOG_OVERLAY};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${getLayer('modal')};

  transition: all 0.3s ease-in-out;
  opacity: 0;
  pointer-events: none;
  ${dialogWrapperAnimate}
`

const dialogContentAnimate = (props: TransitionProps) => {
  const { state } = props
  if (state === 'entered') {
    return css`
      transform: translateY(0);
    `
  }
  return css`
    transform: translateY(-200px);
  `
}

export const DialogContent = styled.div<TransitionProps>`
  width: 100%;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  ${(props) => props.position === 'center' && 'justify-content: center;'}
  ${(props) => props.position === 'top' && 'justify-content: flex-start;'}
  ${(props) => props.position === 'bottom' && 'justify-content: flex-end;'}
  align-items: center;
  transition: all 0.3s ease-in-out;
  transform: translateY(-200px);
  ${dialogContentAnimate}
`

const DialogHeader = styled.div<{
  hasTitle?: boolean
  titleAlign?: 'center' | 'left'
}>`
  position: relative;
  display: flex;
  justify-content: ${(p) => {
    if (!p.hasTitle) return 'flex-end'
    if (p.titleAlign === 'center') return 'center'
    return 'space-between'
  }};
  align-items: center;
  height: 72px;
  padding: 0 40px;
  border-bottom: ${(p) =>
    p.hasTitle ? `1px solid ${p.theme.BORDER_GRAY}` : 'none'};
  box-sizing: border-box;
  flex-shrink: 0;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    padding: 0 16px;
  }
`

const DialogTitle = styled.h2<{ align?: 'center' | 'left' }>`
  font-style: normal;
  font-weight: 700;
  font-size: ${(p) => p.theme.font.size.l};
  line-height: 24px;
  margin: 0;
  color: #463e28;
  text-align: ${(p) => p.align || 'center'};
`

const CloseIconWrapper = styled.div<{ titleAlign?: 'center' | 'left' }>`
  cursor: pointer;
  display: flex;
  justify-content: flex-end;
  ${(p) =>
    p.titleAlign === 'center' &&
    css`
      position: absolute;
      right: 40px;
    `}

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    ${(p) =>
      p.titleAlign === 'center' &&
      css`
        right: 16px;
      `}

    img {
      width: 18px;
      height: 18px;
    }
  }
`

const DialogBodyContent = styled.div<{ hasTitle?: boolean }>`
  padding: ${(p) => (p.hasTitle ? '24px 24px 96px 24px' : '0 24px 96px 24px')};
  overflow-y: auto;
  flex: 1;
  box-sizing: border-box;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    padding: ${(p) =>
      p.hasTitle ? '16px 16px 88px 16px' : '0 16px 88px 16px'};
  }
`

const DialogFooter = styled.div<{ layout?: 'right' | 'spaceBetween' }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 72px;
  padding: 0 40px;

  border-top: 1px solid ${(p) => p.theme.BORDER_GRAY};
  background-color: #ffffff;
  border-radius: 0 0 10px 10px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: ${(p) =>
    p.layout === 'spaceBetween' ? 'space-between' : 'flex-end'};
  gap: ${(p) => (p.layout === 'spaceBetween' ? '20px' : '12px')};

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    padding: 0 16px;
    border-radius: 0;
  }
`

interface DialogBodyProps {
  height?: number
}

const DialogBody = styled.div<DialogBodyProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 640px;
  margin: 100px 25px 100px;
  background: #ffffff;
  border-radius: 10px;
  max-height: calc(100dvh - 200px);
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    width: 100vw;
    max-width: 640px;
    margin: 100px 0 0 0;
    height: calc(100dvh - 100px);
    max-height: calc(100dvh - 100px);
    border-radius: 10px 10px 0 0;
  }
`

export interface ModalProps {
  show?: boolean
  onClose: () => void
  children: React.ReactNode
  className?: string
  disableClose?: boolean
  styleOptions?: {
    height?: number
    position?: 'top' | 'center' | 'bottom'
  }
}

interface DialogV2Component extends React.FC<ModalProps> {
  Header: React.FC<HeaderProps>
  Body: React.FC<BodyProps>
  Footer: React.FC<FooterProps>
}

interface HeaderProps {
  title?: string
  showCloseIcon?: boolean
  titleAlign?: 'center' | 'left'
  onClose?: () => void
  disableClose?: boolean
}

interface BodyProps {
  children: React.ReactNode
  hasHeader?: boolean
}

interface FooterProps {
  children: React.ReactNode
  layout?: 'right' | 'spaceBetween'
}

const DialogV2: React.FC<ModalProps> = ({
  show,
  onClose,
  children,
  className,
  disableClose,
  styleOptions,
}) => {
  // モーダル表示時、一時的にbodyのスクロールを無効にする。
  useEffect(() => {
    if (show) {
      document.body.style.top = `-${window.scrollY}px`
      document.body.style.left = '0px'
      document.body.style.right = '0px'
      document.body.style.position = 'fixed'
    } else {
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1)
    }
  }, [show])

  const nodeRef = React.useRef(null)
  const component = (
    <Transition
      in={show}
      nodeRef={nodeRef}
      unmountOnExit
      timeout={{ enter: 0, exit: 350 }}
    >
      {(state: TransitionStatus) => (
        <DialogWrapper
          onMouseDown={disableClose ? undefined : onClose}
          state={state}
          className={className}
          role="dialog"
          aria-label="backdrop"
        >
          <DialogContent
            onMouseDown={(e) => e.stopPropagation()}
            state={state}
            position={styleOptions?.position}
          >
            <DialogBody height={styleOptions?.height}>{children}</DialogBody>
          </DialogContent>
        </DialogWrapper>
      )}
    </Transition>
  )

  return ReactDOM.createPortal(
    component,
    document.querySelector('#root') || document.body
  )
}

// Compound Component pattern: サブコンポーネント

const Header: React.FC<HeaderProps> = ({
  title,
  showCloseIcon = true,
  titleAlign = 'center',
  onClose,
  disableClose = false,
}) => {
  return (
    <DialogHeader hasTitle={!!title} titleAlign={titleAlign}>
      {title && <DialogTitle align={titleAlign}>{title}</DialogTitle>}
      {showCloseIcon && onClose && (
        <CloseIconWrapper
          titleAlign={titleAlign}
          onClick={disableClose ? undefined : onClose}
          style={{
            opacity: disableClose ? 0.5 : 1,
            cursor: disableClose ? 'not-allowed' : 'pointer',
          }}
        >
          <img src="/image/close.svg" alt="close" width={24} height={24} />
        </CloseIconWrapper>
      )}
    </DialogHeader>
  )
}

const Body: React.FC<BodyProps> = ({ children, hasHeader = true }) => {
  return <DialogBodyContent hasTitle={hasHeader}>{children}</DialogBodyContent>
}

const Footer: React.FC<FooterProps> = ({ children, layout = 'right' }) => {
  return <DialogFooter layout={layout}>{children}</DialogFooter>
}

const DialogV2WithSubComponents = DialogV2 as DialogV2Component
DialogV2WithSubComponents.Header = Header
DialogV2WithSubComponents.Body = Body
DialogV2WithSubComponents.Footer = Footer

export default DialogV2WithSubComponents
